/**
 * FormField — HOC envolvente para todo control de formulario. Compone label +
 * control + helper + error y conecta aria-describedby/aria-invalid/id en el
 * único hijo. Sigue §3.23 del Design System y §4.3 de PROMPT_MAESTRO.
 */
import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';

export type FormFieldVariant = 'stacked' | 'inline';

export interface FormFieldProps {
  /** Etiqueta del campo. */
  label: ReactNode;
  /** Si true, oculta visualmente el label (queda accesible para screen readers). */
  hideLabel?: boolean;
  /** Texto auxiliar bajo el control. */
  helper?: ReactNode;
  /** Mensaje de error inline; su presencia marca aria-invalid en el hijo. */
  error?: ReactNode;
  /** Marca el campo como requerido (asterisco + sr-only "(requerido)"). */
  required?: boolean;
  /** Renderiza "(opcional)" junto al label. Ignorado si required=true. */
  optional?: boolean;
  /** Variante de layout. */
  variant?: FormFieldVariant;
  /** id explícito para el control hijo; si se omite se autogenera. */
  htmlFor?: string;
  /** Clase aplicada al wrapper externo. */
  className?: string;
  /** Un único control React: Input, Select, Textarea, Checkbox, Radio, Switch. */
  children: ReactElement;
}

// Forma esperada de las props inyectadas al hijo
interface InjectedProps {
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  required?: boolean;
}

export const FormField = ({
  label,
  hideLabel = false,
  helper,
  error,
  required,
  optional,
  variant = 'stacked',
  htmlFor,
  className,
  children,
}: FormFieldProps) => {
  const reactId = useId();
  const controlId = htmlFor ?? `field-${reactId}`;
  const helperId = helper ? `${controlId}-helper` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const isInvalid = Boolean(error);

  // Solo se admite un único hijo (regla del Design System §3.23)
  const child = Children.only(children);
  if (!isValidElement(child)) {
    // En dev avisar con throw; en prod degradar silenciosamente devolviendo el hijo
    if (import.meta.env?.DEV) {
      throw new Error('FormField requiere un único hijo React válido.');
    }
    return <>{children}</>;
  }

  // Tipado interno para acceder a las props existentes del hijo sin perder seguridad
  const childProps = (child.props ?? {}) as Record<string, unknown>;

  // Compone el aria-describedby del hijo respetando el ya provisto
  const existingDescribedBy = childProps['aria-describedby'] as string | undefined;
  const describedBy =
    [existingDescribedBy, helperId, errorId].filter(Boolean).join(' ') || undefined;

  const injected: InjectedProps = {
    id: (childProps.id as string | undefined) ?? controlId,
    'aria-invalid': isInvalid || (childProps['aria-invalid'] as boolean | undefined),
    'aria-describedby': describedBy,
    required: required ?? (childProps.required as boolean | undefined),
  };

  const labelEl = (
    <label
      htmlFor={(childProps.id as string | undefined) ?? controlId}
      className={cn(
        'block text-caption font-medium text-primary-900',
        hideLabel && 'sr-only',
        variant === 'stacked' ? 'mb-2' : 'mb-0',
      )}
    >
      {label}
      {required && (
        <>
          <span className="ml-1 text-error" aria-hidden="true">
            *
          </span>
          <span className="sr-only"> (requerido)</span>
        </>
      )}
      {optional && !required && (
        // F8.2 a11y: `mute-500` sobre cream falla AA (~2.9:1).
        // `primary-900/70` pasa AA cómodamente para texto pequeño auxiliar.
        <span className="ml-1 text-caption text-primary-900/70">(opcional)</span>
      )}
    </label>
  );

  const controlEl = cloneElement(child, injected as Record<string, unknown>);

  const messages = (
    <>
      {error ? (
        <p id={errorId} role="alert" className="mt-1 text-caption text-error">
          {error}
        </p>
      ) : helper ? (
        // F8.2 a11y: helper text usa `primary-900/70` para pasar AA sobre cream.
        <p id={helperId} className="mt-1 text-caption text-primary-900/70">
          {helper}
        </p>
      ) : null}
    </>
  );

  if (variant === 'inline') {
    return (
      <div
        className={cn(
          'grid w-full items-start gap-3 sm:grid-cols-[minmax(8rem,12rem)_1fr]',
          className,
        )}
      >
        <div className="pt-2">{labelEl}</div>
        <div>
          {controlEl}
          {messages}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {labelEl}
      {controlEl}
      {messages}
    </div>
  );
};

FormField.displayName = 'FormField';
